/**
 * SLA Policies Page
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Pencil,
  Trash2,
  Clock,
  ChevronDown,
  ChevronUp,
  Shield,
} from 'lucide-react';

interface SlaPolicy {
  id: string;
  name: string;
  applicationId: string | null;
  applicationName: string | null;
  priority: string;
  responseTimeMinutes: number;
  resolutionTimeMinutes: number;
  businessHoursOnly: boolean;
  isActive: boolean;
  createdAt: string;
}

interface EscalationRule {
  id: string;
  slaPolicyId: string;
  type: string;
  thresholdPercent: number;
  action: string;
  notifyUserId: string | null;
  notifyUserName: string | null;
  notifyRole: string | null;
}

interface Application {
  id: string;
  name: string;
}

const priorityLabels: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

const priorityColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600',
};

const emptyPolicyForm = {
  name: '',
  applicationId: '',
  priority: 'medium',
  responseTimeMinutes: '60',
  resolutionTimeMinutes: '480',
  businessHoursOnly: false,
};

const emptyEscalationForm = {
  type: 'response',
  thresholdPercent: '75',
  action: 'notify',
  notifyRole: '',
};

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (remaining === 0) return `${hours}h`;
  return `${hours}h ${remaining}m`;
}

export function SlaPoliciesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<SlaPolicy | null>(null);
  const [policyForm, setPolicyForm] = useState(emptyPolicyForm);
  const [expandedPolicyId, setExpandedPolicyId] = useState<string | null>(null);
  const [escalationDialogOpen, setEscalationDialogOpen] = useState(false);
  const [escalationPolicyId, setEscalationPolicyId] = useState<string | null>(null);
  const [escalationForm, setEscalationForm] = useState(emptyEscalationForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState<SlaPolicy | null>(null);

  // Fetch policies
  const { data: policiesData, isLoading } = useQuery({
    queryKey: ['sla-policies'],
    queryFn: async () => {
      const result = await api.getSlaPolicies({ limit: 100 });
      return result;
    },
  });

  // Fetch applications for the select dropdown
  const { data: applicationsData } = useQuery({
    queryKey: ['finance-applications'],
    queryFn: async () => {
      const result = await api.getFinanceApplications();
      return result;
    },
  });

  const policies = (policiesData?.data || []) as SlaPolicy[];
  const applications = (applicationsData?.data || []) as Application[];

  // Create / Update policy mutations
  const createPolicyMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createSlaPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sla-policies'] });
      closePolicyDialog();
      toast({ title: 'SLA policy created' });
    },
    onError: () => {
      toast({ title: 'Failed to create policy', variant: 'destructive' });
    },
  });

  const updatePolicyMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateSlaPolicy(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sla-policies'] });
      closePolicyDialog();
      toast({ title: 'SLA policy updated' });
    },
    onError: () => {
      toast({ title: 'Failed to update policy', variant: 'destructive' });
    },
  });

  const deletePolicyMutation = useMutation({
    mutationFn: (id: string) => api.deleteSlaPolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sla-policies'] });
      setDeleteDialogOpen(false);
      setDeletingPolicy(null);
      toast({ title: 'SLA policy deleted' });
    },
    onError: () => {
      toast({ title: 'Failed to delete policy', variant: 'destructive' });
    },
  });

  const createEscalationMutation = useMutation({
    mutationFn: ({ policyId, data }: { policyId: string; data: Record<string, unknown> }) =>
      api.createEscalationRule(policyId, data),
    onSuccess: () => {
      if (escalationPolicyId) {
        queryClient.invalidateQueries({ queryKey: ['escalation-rules', escalationPolicyId] });
      }
      setEscalationDialogOpen(false);
      setEscalationForm(emptyEscalationForm);
      toast({ title: 'Escalation rule created' });
    },
    onError: () => {
      toast({ title: 'Failed to create escalation rule', variant: 'destructive' });
    },
  });

  const deleteEscalationMutation = useMutation({
    mutationFn: (ruleId: string) => api.deleteEscalationRule(ruleId),
    onSuccess: () => {
      if (expandedPolicyId) {
        queryClient.invalidateQueries({ queryKey: ['escalation-rules', expandedPolicyId] });
      }
      toast({ title: 'Escalation rule deleted' });
    },
  });

  function openCreatePolicyDialog() {
    setEditingPolicy(null);
    setPolicyForm(emptyPolicyForm);
    setPolicyDialogOpen(true);
  }

  function openEditPolicyDialog(policy: SlaPolicy) {
    setEditingPolicy(policy);
    setPolicyForm({
      name: policy.name,
      applicationId: policy.applicationId || '',
      priority: policy.priority,
      responseTimeMinutes: String(policy.responseTimeMinutes),
      resolutionTimeMinutes: String(policy.resolutionTimeMinutes),
      businessHoursOnly: policy.businessHoursOnly,
    });
    setPolicyDialogOpen(true);
  }

  function closePolicyDialog() {
    setPolicyDialogOpen(false);
    setEditingPolicy(null);
    setPolicyForm(emptyPolicyForm);
  }

  function handlePolicySubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: Record<string, unknown> = {
      name: policyForm.name,
      applicationId: policyForm.applicationId || null,
      priority: policyForm.priority,
      responseTimeMinutes: parseInt(policyForm.responseTimeMinutes) || 60,
      resolutionTimeMinutes: parseInt(policyForm.resolutionTimeMinutes) || 480,
      businessHoursOnly: policyForm.businessHoursOnly,
    };

    if (editingPolicy) {
      updatePolicyMutation.mutate({ id: editingPolicy.id, data: payload });
    } else {
      createPolicyMutation.mutate(payload);
    }
  }

  function openEscalationDialog(policyId: string) {
    setEscalationPolicyId(policyId);
    setEscalationForm(emptyEscalationForm);
    setEscalationDialogOpen(true);
  }

  function handleEscalationSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!escalationPolicyId) return;
    createEscalationMutation.mutate({
      policyId: escalationPolicyId,
      data: {
        type: escalationForm.type,
        thresholdPercent: parseInt(escalationForm.thresholdPercent) || 75,
        action: escalationForm.action,
        notifyRole: escalationForm.notifyRole || null,
      },
    });
  }

  function toggleExpand(policyId: string) {
    setExpandedPolicyId(expandedPolicyId === policyId ? null : policyId);
  }

  const isSaving = createPolicyMutation.isPending || updatePolicyMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">SLA Policies</h1>
          <p className="text-muted-foreground">
            Manage service level agreements and escalation rules
          </p>
        </div>
        <Button onClick={openCreatePolicyDialog}>
          <Plus className="h-4 w-4 mr-2" />
          New Policy
        </Button>
      </div>

      {/* Policies List */}
      {policies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No SLA policies</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first SLA policy to define response and resolution time targets.
            </p>
            <Button onClick={openCreatePolicyDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Create Policy
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {policies.map((policy) => (
            <PolicyCard
              key={policy.id}
              policy={policy}
              isExpanded={expandedPolicyId === policy.id}
              onToggleExpand={() => toggleExpand(policy.id)}
              onEdit={() => openEditPolicyDialog(policy)}
              onDelete={() => {
                setDeletingPolicy(policy);
                setDeleteDialogOpen(true);
              }}
              onAddEscalation={() => openEscalationDialog(policy.id)}
              onDeleteEscalation={(ruleId) => deleteEscalationMutation.mutate(ruleId)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Policy Dialog */}
      <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPolicy ? 'Edit SLA Policy' : 'New SLA Policy'}</DialogTitle>
            <DialogDescription>
              {editingPolicy
                ? 'Update the SLA policy settings.'
                : 'Define response and resolution time targets for tickets.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePolicySubmit} className="space-y-4">
            <div>
              <Label>Policy Name *</Label>
              <Input
                value={policyForm.name}
                onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })}
                placeholder="e.g. Premium Support SLA"
                required
              />
            </div>

            <div>
              <Label>Application</Label>
              <Select
                value={policyForm.applicationId || 'none'}
                onValueChange={(v) =>
                  setPolicyForm({ ...policyForm, applicationId: v === 'none' ? '' : v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select application" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">All Applications</SelectItem>
                  {applications.map((app) => (
                    <SelectItem key={app.id} value={app.id}>
                      {app.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Priority</Label>
              <Select
                value={policyForm.priority}
                onValueChange={(v) => setPolicyForm({ ...policyForm, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Response Time (minutes) *</Label>
                <Input
                  type="number"
                  min="1"
                  value={policyForm.responseTimeMinutes}
                  onChange={(e) =>
                    setPolicyForm({ ...policyForm, responseTimeMinutes: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label>Resolution Time (minutes) *</Label>
                <Input
                  type="number"
                  min="1"
                  value={policyForm.resolutionTimeMinutes}
                  onChange={(e) =>
                    setPolicyForm({ ...policyForm, resolutionTimeMinutes: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="businessHoursOnly"
                checked={policyForm.businessHoursOnly}
                onChange={(e) =>
                  setPolicyForm({ ...policyForm, businessHoursOnly: e.target.checked })
                }
                className="rounded border-gray-300"
              />
              <Label htmlFor="businessHoursOnly" className="mb-0">
                Business hours only
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closePolicyDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || !policyForm.name}>
                {isSaving
                  ? 'Saving...'
                  : editingPolicy
                  ? 'Update Policy'
                  : 'Create Policy'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete SLA Policy</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingPolicy?.name}</strong>? All
              associated escalation rules will also be removed. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingPolicy && deletePolicyMutation.mutate(deletingPolicy.id)}
              disabled={deletePolicyMutation.isPending}
            >
              {deletePolicyMutation.isPending ? 'Deleting...' : 'Delete Policy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalation Rule Dialog */}
      <Dialog open={escalationDialogOpen} onOpenChange={setEscalationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Escalation Rule</DialogTitle>
            <DialogDescription>
              Define when and how to escalate when SLA thresholds are approached.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEscalationSubmit} className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select
                value={escalationForm.type}
                onValueChange={(v) =>
                  setEscalationForm({ ...escalationForm, type: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="response">Response Time</SelectItem>
                  <SelectItem value="resolution">Resolution Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Threshold (%)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={escalationForm.thresholdPercent}
                onChange={(e) =>
                  setEscalationForm({ ...escalationForm, thresholdPercent: e.target.value })
                }
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Trigger when this percentage of the SLA time has elapsed.
              </p>
            </div>

            <div>
              <Label>Action</Label>
              <Select
                value={escalationForm.action}
                onValueChange={(v) =>
                  setEscalationForm({ ...escalationForm, action: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="notify">Notify</SelectItem>
                  <SelectItem value="reassign">Reassign</SelectItem>
                  <SelectItem value="escalate">Escalate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notify Role</Label>
              <Input
                value={escalationForm.notifyRole}
                onChange={(e) =>
                  setEscalationForm({ ...escalationForm, notifyRole: e.target.value })
                }
                placeholder="e.g. manager, admin"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEscalationDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createEscalationMutation.isPending}>
                {createEscalationMutation.isPending ? 'Creating...' : 'Add Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inner component for each policy card with its escalation rules
function PolicyCard({
  policy,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddEscalation,
  onDeleteEscalation,
}: {
  policy: SlaPolicy;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddEscalation: () => void;
  onDeleteEscalation: (ruleId: string) => void;
}) {
  const { data: escalationsData } = useQuery({
    queryKey: ['escalation-rules', policy.id],
    queryFn: async () => {
      const result = await api.getEscalationRules(policy.id);
      return result;
    },
    enabled: isExpanded,
  });

  const escalations = (escalationsData?.data || []) as EscalationRule[];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-medium">{policy.name}</h3>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  priorityColors[policy.priority]
                }`}
              >
                {priorityLabels[policy.priority] || policy.priority}
              </span>
              {policy.isActive ? (
                <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                  Active
                </Badge>
              ) : (
                <Badge variant="secondary" className="bg-gray-100 text-gray-500 text-xs">
                  Inactive
                </Badge>
              )}
              {policy.businessHoursOnly && (
                <Badge variant="outline" className="text-xs">
                  Business Hours
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {policy.applicationName || 'All Applications'}
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Response: <strong>{formatMinutes(policy.responseTimeMinutes)}</strong>
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Resolution: <strong>{formatMinutes(policy.resolutionTimeMinutes)}</strong>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onToggleExpand}>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Expanded escalation rules */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium">Escalation Rules</h4>
              <Button variant="outline" size="sm" onClick={onAddEscalation}>
                <Plus className="h-3 w-3 mr-1" />
                Add Escalation
              </Button>
            </div>

            {escalations.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No escalation rules defined. Add rules to automate SLA breach handling.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Threshold</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Notify</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {escalations.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {rule.type === 'response' ? 'Response' : 'Resolution'}
                        </Badge>
                      </TableCell>
                      <TableCell>{rule.thresholdPercent}%</TableCell>
                      <TableCell className="capitalize">{rule.action}</TableCell>
                      <TableCell>
                        {rule.notifyUserName || rule.notifyRole || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => onDeleteEscalation(rule.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
